import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { TriangleAlert } from 'lucide-react';

import { updateProject } from '../../api/projects';
import { queryKeys } from '../../api/queryKeys';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Select, type SelectOption } from '../../components/Select';
import { Skeleton } from '../../components/Skeleton';
import { Textarea } from '../../components/Textarea';
import { useToast } from '../../components/Toast';
import { useProject } from '../../hooks/useProject';
import { humanizeError } from '../../lib/errors';
import type { BoardType } from '../../lib/types';
import { boardTypeLabels } from '../../i18n/enums';
import styles from './SettingsPages.module.css';

const BOARD_TYPE_OPTIONS: readonly SelectOption<BoardType>[] = (['kanban', 'scrum'] as const).map((value) => ({
  value,
  label: boardTypeLabels[value],
}));

/** Основные настройки проекта: имя, описание, тип доски (ключ неизменяем). */
export function GeneralSettingsPage() {
  const { key = '' } = useParams<{ key: string }>();
  const { project, isLoading } = useProject(key);
  const queryClient = useQueryClient();
  const toast = useToast();

  const [name, setName] = useState<string | null>(null);
  const [description, setDescription] = useState<string | null>(null);
  const [boardType, setBoardType] = useState<BoardType | null>(null);
  const [error, setError] = useState<string | null>(null);

  const currentName = name ?? project?.name ?? '';
  const currentDescription = description ?? project?.description ?? '';
  const currentBoardType = boardType ?? project?.boardType ?? 'kanban';

  const mutation = useMutation({
    mutationFn: () =>
      updateProject(key, { name: currentName.trim(), description: currentDescription, boardType: currentBoardType }),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects.detail(key) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.me });
      toast.success('Настройки сохранены');
    },
    onError: (cause: unknown) => {
      const human = humanizeError(cause);
      setError(human.message);
      toast.error(human.message, { code: human.code });
    },
  });

  const isDirty =
    project !== null &&
    (currentName !== project.name ||
      currentDescription !== project.description ||
      currentBoardType !== project.boardType);

  if (isLoading) {
    return <Skeleton variant="row" count={5} />;
  }

  return (
    <form
      className={styles['form']}
      onSubmit={(event) => {
        event.preventDefault();
        if (currentName.trim().length === 0) {
          setError('Введите название проекта');
          return;
        }
        mutation.mutate();
      }}
    >
      <Input label="Ключ проекта" value={key} disabled hint="Ключ неизменяем после создания" />

      <Input
        label="Название"
        value={currentName}
        maxLength={120}
        error={error}
        onChange={(event) => setName(event.target.value)}
      />

      <Textarea
        label="Описание"
        rows={5}
        value={currentDescription}
        onChange={(event) => setDescription(event.target.value)}
      />

      <Select
        label="Тип доски"
        options={BOARD_TYPE_OPTIONS}
        value={currentBoardType}
        onChange={(value) => setBoardType(value)}
      />

      {project?.boardType === 'scrum' && currentBoardType === 'kanban' ? (
        <p className={styles['warning']} role="alert">
          <TriangleAlert aria-hidden size={16} />
          Переход на канбан отключит бэклог и спринты. Активный спринт должен быть завершён.
        </p>
      ) : null}

      <div className={styles['actions']}>
        <Button type="submit" variant="primary" loading={mutation.isPending} disabled={!isDirty && !mutation.isError}>
          Сохранить
        </Button>
      </div>
    </form>
  );
}
